import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import { InfiniteScrollList } from '@/components/ui/infinite-scroll-list';
import { legalReportingApi } from '@/lib/pillarApi';
import type { OutstandingSignature, OutstandingSignatureState } from '@pillar-portal/api-types';
import {
    OUTSTANDING_SIGNATURE_STATES,
    SYSTEM_DISPLAY_TIME_ZONE,
    formatTimestampForMerchantUi,
} from '@pillar-portal/api-types';
import { LegalCsvExportButton } from './LegalCsvExportButton';
import { collectAllPages, toCsv } from './legal-csv-export';

const signatureStateLabels: Record<OutstandingSignatureState, string> = {
    unsigned: 'Not signed',
    expired: 'Signature expired',
    expiring_soon: 'Signature expiring soon',
};

function describeLatestSignature(row: OutstandingSignature): string {
    if (row.latest_signature === null) {
        return 'No PRINCIPAL has signed it yet.';
    }
    const signedOn = formatTimestampForMerchantUi(
        row.latest_signature.accepted_ts,
        SYSTEM_DISPLAY_TIME_ZONE
    );
    const expiresOn = formatTimestampForMerchantUi(
        row.latest_signature.expires_ts,
        SYSTEM_DISPLAY_TIME_ZONE
    );
    return `Signed by ${row.latest_signature.signer.primary_email} on ${signedOn}; ${
        row.signature_state === 'expired' ? 'expired' : 'expires'
    } on ${expiresOn}.`;
}

function signatureStatesRequest(selectedStates: ReadonlyArray<OutstandingSignatureState>): {
    signature_states?: OutstandingSignatureState[];
} {
    return selectedStates.length === OUTSTANDING_SIGNATURE_STATES.length
        ? {}
        : { signature_states: [...selectedStates] };
}

async function buildOutstandingSignaturesCsv(
    selectedStates: ReadonlyArray<OutstandingSignatureState>
): Promise<{ csv: string; truncated: boolean }> {
    const { rows, truncated } = await collectAllPages(async (page) => {
        const response = await legalReportingApi.searchOutstandingSignaturesPage({
            ...page,
            ...signatureStatesRequest(selectedStates),
        });
        return { items: response.outstanding_signatures, pagination: response.pagination };
    });
    const csv = toCsv(
        [
            'Organization',
            'Agreement',
            'Version',
            'State',
            'Last signed at (UTC)',
            'Last signer',
            'Signature expires at (UTC)',
        ],
        rows.map((row) => [
            row.organization.organization_name,
            row.legal_document.legal_document_title,
            row.legal_document.legal_document_version,
            signatureStateLabels[row.signature_state],
            row.latest_signature?.accepted_ts ?? null,
            row.latest_signature?.signer.primary_email ?? null,
            row.latest_signature?.expires_ts ?? null,
        ])
    );
    return { csv, truncated };
}

/**
 * Organizations whose in-force Organization agreements still need a
 * PRINCIPAL's signature. Customer success activates services from this list.
 */
export function OutstandingSignaturesPage() {
    const [selectedStates, setSelectedStates] = useState<ReadonlyArray<OutstandingSignatureState>>(
        OUTSTANDING_SIGNATURE_STATES
    );
    const queryKeyPrefix = useMemo(
        () => ['legalOutstandingSignatures', selectedStates.join(',')] as const,
        [selectedStates]
    );

    return (
        <div className="p-6 max-w-5xl mx-auto">
            <InfiniteScrollList<OutstandingSignature, string, typeof queryKeyPrefix>
                pageTitle="Outstanding signatures"
                queryPlaceholder="Search by Organization or agreement"
                queryKeyPrefix={queryKeyPrefix}
                pageSize={25}
                enabled={selectedStates.length > 0}
                headerActions={
                    selectedStates.length > 0 ? (
                        <LegalCsvExportButton
                            filename="outstanding-signatures.csv"
                            buildCsv={async () =>
                                await buildOutstandingSignaturesCsv(selectedStates)
                            }
                        />
                    ) : null
                }
                controls={
                    <fieldset className="flex flex-wrap gap-4 text-sm">
                        <legend className="sr-only">Signature state</legend>
                        {OUTSTANDING_SIGNATURE_STATES.map((state) => (
                            <label key={state} className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={selectedStates.includes(state)}
                                    onChange={(event) => {
                                        const checked = event.target.checked;
                                        setSelectedStates((current) =>
                                            OUTSTANDING_SIGNATURE_STATES.filter((candidate) =>
                                                candidate === state
                                                    ? checked
                                                    : current.includes(candidate)
                                            )
                                        );
                                    }}
                                />
                                {signatureStateLabels[state]}
                            </label>
                        ))}
                    </fieldset>
                }
                fetchPage={async (params) => {
                    const response = await legalReportingApi.searchOutstandingSignaturesPage({
                        ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                        limit: params.limit,
                        cursor: params.cursor,
                        ...signatureStatesRequest(selectedStates),
                    });
                    return {
                        items: response.outstanding_signatures,
                        pagination: response.pagination,
                    };
                }}
                parseCursorParam={(pageParam: unknown) =>
                    pageParam === null || pageParam === undefined
                        ? undefined
                        : z.string().parse(pageParam)
                }
                stateCopy={{
                    loadingLabel: 'Loading outstanding signatures...',
                    emptyLabel:
                        selectedStates.length === 0
                            ? 'Choose at least one signature state.'
                            : 'Every in-force Organization agreement is signed.',
                    endOfListLabel: 'End of outstanding signatures.',
                    genericErrorLabel: 'Unable to load outstanding signatures.',
                }}
                renderItems={({ items }) => (
                    <div className="space-y-2">
                        {items.map((row) => (
                            <div
                                key={row.legal_document_assignment_uuid}
                                className="rounded border p-3 text-sm"
                            >
                                <div className="flex flex-wrap items-baseline justify-between gap-2">
                                    <Link
                                        className="font-medium underline"
                                        to={`/organizations/${row.organization.organization_uuid}`}
                                    >
                                        {row.organization.organization_name}
                                    </Link>
                                    <span className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-900">
                                        {signatureStateLabels[row.signature_state]}
                                    </span>
                                </div>
                                <div>
                                    {row.legal_document.legal_document_title} (
                                    {row.legal_document.legal_document_version})
                                </div>
                                <div className="text-gray-600">{describeLatestSignature(row)}</div>
                            </div>
                        ))}
                    </div>
                )}
            />
        </div>
    );
}
